import React, { useState, memo, useMemo, useCallback, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus, oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Copy, Check } from 'lucide-react';
import AuthImage from './chat/AuthImage';
import rehypeRaw from "rehype-raw";
import { appImages } from '../utils/constants';
import ExcelPreviewModal from './chat/modals/ExcelPreviewModal';
import toast from 'react-hot-toast';
import { FiEye, FiLoader } from 'react-icons/fi';
import { API_URL } from '../services/api';

// Hoisted to module scope — stable references so React.memo is never defeated
const REMARK_PLUGINS = [remarkGfm, remarkMath];
const REHYPE_PLUGINS = [rehypeKatex, rehypeRaw];

const NotificationChip = ({ event, app }) => {
  return (
    <div className="relative group bg-zinc-800 h-8 border border-zinc-700 rounded-full flex items-center gap-2 p-1.5 pr-3 text-xs text-zinc-300 shadow-md animate-fade-in-fast">
      <img src={appImages[app]} alt={app} className="h-5 w-5 rounded-full" />
      <button className="flex-1 text-left truncate max-w-[200px]">
        <span className="font-medium">{event}</span>
      </button>
    </div>
  );
};

function CodeBlockWithCopy({ language, codeString, syntaxHighlighterProps, lightTheme = false }) {
  const [isCopied, setIsCopied] = useState(false);

  const handleCopy = () => {
    if (isCopied) return;

    navigator.clipboard.writeText(codeString).then(() => {
      setIsCopied(true);
      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    }).catch(err => {
      console.error('Failed to copy code to clipboard:', err);
    });
  };

  return (
    <div className="relative group code-block-container">
      {/* Copy Button */}
      <button
        onClick={handleCopy}
        aria-label={isCopied ? 'Code copied' : 'Copy code'}
        className={`absolute top-2 right-2 z-10 p-1.5 rounded-md transition-opacity duration-200 ease-in-out
                    ${isCopied
            ? 'bg-green-700 text-white'
            : 'bg-gray-700 text-gray-300 hover:bg-gray-600 opacity-0 group-hover:opacity-100 focus:opacity-100'
          }`}
      >
        {isCopied ? <Check size={16} /> : <Copy size={16} />}
      </button>

      {/* Syntax Highlighter */}
      <SyntaxHighlighter
        style={lightTheme ? oneLight : vscDarkPlus} // Use oneLight for light theme, vscDarkPlus for dark theme
        language={language}
        PreTag="div" // You are using "div" as the PreTag.
        {...syntaxHighlighterProps} // Pass down any props received
        customStyle={{
          border: '0.1px solid #555',
          // Styles here apply to the PreTag="div" container
          // Padding adjustment might be needed if copy button overlaps too much
          // paddingTop: '2.5rem', // Uncomment and adjust if needed
        }}
        wrapLines={true} // Enable line wrapping (applies white-space: pre-wrap)
        codeTagProps={{  // Props for the <code> tag that wraps the actual code content
          style: {
            // Ensures long unbroken strings within a line will also break and wrap.
            // overflowWrap: 'break-word', // Standard word breaking
            // Alternatively, for more aggressive breaking:
            // wordBreak: 'break-all',

            // `white-space: 'pre-wrap'` should be handled by `wrapLines={true}`.
            // If you find it's not, you can explicitly add it here:
            // whiteSpace: 'pre-wrap',
          }
        }}
      >
        {codeString}
      </SyntaxHighlighter>
    </div>
  );
}

const MarkdownRenderer = memo(function MarkdownRenderer({ markdown, lightTheme = false, isAssistant = false, userGoogleId }) {
  const [isCopied, setIsCopied] = useState(false);
  const userGoogleIdRef = useRef(userGoogleId);
  userGoogleIdRef.current = userGoogleId;
  const [processingFileId, setProcessingFileId] = useState(null);
  const [excelPreviewModal, setExcelPreviewModal] = useState({ isOpen: false, file: null });

  const getFileForPreview = useCallback(async (accessToken, url) => {
    console.log("[getFileForPreview] Original URL passed in:", url);
    let fetchUrl = url.startsWith('/') ? `${API_URL}${url}` : url;

    const isDirectS3 = url.includes('amazonaws.com');
    console.log("[getFileForPreview] Is direct S3 link:", isDirectS3);

    // If it's a direct S3 link, use the backend proxy to bypass Amazon's CORS restrictions
    if (isDirectS3) {
      fetchUrl = `${API_URL}/file/stream-s3?s3_url=${encodeURIComponent(url)}`;
      console.log("[getFileForPreview] Using proxy for S3 link:", fetchUrl);
    } else {
      // Automatically extract MongoDB ObjectId (24 hex characters) from the URL
      // BUT only do URL rewrites if it's NOT a direct S3 link (because the S3 file ID likely doesn't match the parent file table)
      const idMatch = url.match(/([a-fA-F0-9]{24})/);

      if (idMatch && (url.includes('/file/download') || url.startsWith('/'))) {
        fetchUrl = `${API_URL}/file/download-direct/${idMatch[1]}`;
      }
    }

    console.log("[getFileForPreview] Final fetchUrl:", fetchUrl);

    // Since we are now routing S3 links through our backend proxy as well, we ALWAYS send the Authorization header
    // and include credentials (unless somehow dealing with an unrecognized external domain).
    const isExternal = !fetchUrl.includes(API_URL) && !fetchUrl.startsWith('/') && !fetchUrl.includes(window.location.hostname);

    const options = {
      method: 'GET',
      headers: (accessToken && !isExternal) ? { 'Authorization': `Bearer ${accessToken}` } : {},
      credentials: isExternal ? 'omit' : 'include'
    };
    console.log("[getFileForPreview] Fetch options:", options);

    try {
      let response = await fetch(fetchUrl, options);
      console.log("[getFileForPreview] Initial response status:", response.status);

      if (response.status === 401 && !isExternal) {
        console.error("[getFileForPreview] Unauthorized (401), clearing local storage and redirecting.");
        localStorage.clear();
        window.location.href = '/login';
        return;
      }
      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        console.error(`[getFileForPreview] HTTP Error ${response.status}: ${errorText}`);
        throw new Error(`Error fetching file: ${response.statusText}. ${errorText}`);
      }

      const contentType = response.headers.get("content-type");
      console.log("[getFileForPreview] Content-Type:", contentType);

      if (contentType && contentType.includes("application/json")) {
        // If the backend returns our JSON wrapper with a presigned URL instead of the blob
        const json = await response.json();
        console.log("[getFileForPreview] Received JSON payload:", json);
        if (json.url) {
          console.log("[getFileForPreview] Fetching presigned URL:", json.url);
          response = await fetch(json.url);
          if (!response.ok) throw new Error("Could not fetch presigned URL.");
        } else {
          throw new Error("JSON response did not contain a url field.");
        }
      } else if (contentType && contentType.includes("text/html") && !isExternal) {
        // Stop it from passing HTML into the file parser
        console.error("[getFileForPreview] Received HTML block instead of file. Suspected invalid router path.");
        throw new Error("Received HTML instead of a file blob from the server. The link might be invalid.");
      }

      console.log("[getFileForPreview] Successfully returning blob.");
      return response.blob();
    } catch (error) {
      console.error('[getFileForPreview] File Preview Download Error caught in block:', error);
      throw error;
    }
  }, []);

  const handleExcelPreview = useCallback(async (href, filename) => {
    setProcessingFileId(href);
    try {
      const blob = await getFileForPreview(userGoogleIdRef.current, href);
      const fileObject = new File(
        [blob],
        filename,
        { type: blob.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
      );
      setExcelPreviewModal({ isOpen: true, file: fileObject });
    } catch (error) {
      toast.error("Could not load preview.");
      console.error(error);
    } finally {
      setProcessingFileId(null);
    }
  }, [getFileForPreview]);

  const closeExcelPreview = () => {
    setExcelPreviewModal({ isOpen: false, file: null });
  };

  const handleCopyToClipboard = () => {
    if (isCopied) return;

    navigator.clipboard.writeText(markdown).then(() => {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000); // Reset after 2 seconds
    }).catch(err => {
      console.error('Failed to copy markdown to clipboard:', err);
    });
  };

  // Memoize the components object so ReactMarkdown doesn't re-parse
  // when parent re-renders with the same markdown text.
  const markdownComponents = useMemo(() => ({
    code({ node, inline, className, children, ...props }) {
              const match = /language-(\w+)/.exec(className || '');
              const codeString = String(children).replace(/\n$/, '');

              if (!inline && match) {
                return (
                  <CodeBlockWithCopy
                    language={match?.[1]}
                    codeString={codeString}
                    syntaxHighlighterProps={props}
                    lightTheme={lightTheme}
                  />
                );
              }

              return (
                <code className="px-1.5 py-0.5 bg-zinc-800/60 text-[#e879f9] rounded text-[14px] font-mono border border-zinc-700/50" {...props}>
                  {children}
                </code>
              );
            },

            a({ node, href, children, ...props }) {
              const linkText = String(children);

              // Check if the text or the href ends with .xlsx or .csv
              const isExcelText = linkText.toLowerCase().includes('.xlsx') || linkText.toLowerCase().includes('.csv');
              const isExcelHref = href && (href.toLowerCase().split('?')[0].endsWith('.xlsx') || href.toLowerCase().split('?')[0].endsWith('.csv'));

              if (isExcelText || isExcelHref) {
                const isProcessing = processingFileId === href;
                return (
                  <span className="inline-flex items-center gap-2 align-middle border border-[#60a5fa]/30 bg-[#60a5fa]/10 px-2 py-0.5 rounded cursor-pointer group hover:bg-[#60a5fa]/20 transition-colors my-1">
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#60a5fa] hover:text-[#93c5fd] font-medium transition-colors"
                      {...props}
                    >
                      {children}
                    </a>
                    <button
                      onClick={(e) => { e.preventDefault(); handleExcelPreview(href, linkText.replace(/[^a-zA-Z0-9_.-]/g, '')); }}
                      disabled={processingFileId !== null}
                      className="text-[#60a5fa] opacity-60 group-hover:opacity-100 transition-opacity disabled:opacity-40 ml-1"
                      title="Preview Excel File"
                    >
                      {isProcessing ? <FiLoader className="animate-spin" /> : <FiEye size={16} />}
                    </button>
                  </span>
                );
              }

              return (
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#60a5fa] hover:text-[#93c5fd] underline decoration-[#60a5fa]/40 hover:decoration-[#60a5fa] transition-colors underline-offset-2"
                  {...props}
                >
                  {children}
                </a>
              );
            },

            img({ node, src, alt, ...props }) {
              return <AuthImage src={src} alt={alt || ''} {...props} />;
            },

            event({ node, ...props }) {
              const eventText = props.event || "Event";
              const appText = props.app || "App";

              return (
                <span className="inline-block align-middle">
                  <NotificationChip event={eventText} app={appText} />
                </span>
              );
            },

            h1({ children }) {
              return (
                <h1 className="text-[26px] font-bold text-gray-100 mt-6 mb-3 leading-[1.3]">
                  {children}
                </h1>
              );
            },

            h2({ children }) {
              return (
                <h2 className="text-[22px] font-bold text-gray-100 mt-5 mb-3 leading-[1.35]">
                  {children}
                </h2>
              );
            },

            h3({ children }) {
              return (
                <h3 className="text-[18px] font-semibold text-gray-200 mt-4 mb-2.5 leading-[1.4]">
                  {children}
                </h3>
              );
            },

            h4({ children }) {
              return (
                <h4 className="text-[16px] font-semibold mt-3.5 mb-2 leading-[1.45]">
                  {children}
                </h4>
              );
            },

            h5({ children }) {
              return (
                <h5 className="text-[15px] font-semibold mt-3 mb-2 leading-[1.5]">
                  {children}
                </h5>
              );
            },

            h6({ children }) {
              return (
                <h6 className="text-[14px] font-semibold mt-3 mb-2 leading-[1.5] uppercase tracking-wide">
                  {children}
                </h6>
              );
            },

            p({ children }) {
              return (
                <span className="leading-[1.7] block">
                  {children}
                </span>
              );
            },

            strong({ children }) {
              return (
                <strong className="font-medium">
                  {children}
                </strong>
              );
            },

            em({ children }) {
              return (
                <em className="italic">
                  {children}
                </em>
              );
            },

            del({ children }) {
              return (
                <del className="line-through">
                  {children}
                </del>
              );
            },

            ul({ children }) {
              return (
                <ul className="list-disc list-outside ml-6 mb-4 space-y-1.5">
                  {children}
                </ul>
              );
            },

            ol({ children }) {
              return (
                <ol className="list-decimal list-outside ml-6 mb-4 space-y-1.5">
                  {children}
                </ol>
              );
            },

            li({ children }) {
              return (
                <li className="leading-[1.7] pl-1">
                  {children}
                </li>
              );
            },

            blockquote({ children }) {
              return (
                <blockquote className="border-l-[3px] border-gray-600 bg-zinc-800/40 pl-4 pr-4 py-2.5 my-4 text-gray-300/90">
                  {children}
                </blockquote>
              );
            },

            hr() {
              return (
                <hr className="border-0 border-t border-gray-700/50 my-6" />
              );
            },

            kbd({ children }) {
              return (
                <kbd className="px-2 py-0.5 text-[13px] font-medium text-gray-300 bg-zinc-800/70 border border-gray-700/60 rounded shadow-sm font-mono">
                  {children}
                </kbd>
              );
            },


            table({ children, ...props }) {
              return (
                <div className="overflow-x-auto my-6 border border-zinc-700 rounded-lg">
                  <table className="w-full text-sm text-left border-collapse" {...props}>
                    {children}
                  </table>
                </div>
              );
            },

            thead({ children, ...props }) {
              return (
                <thead
                  className="bg-[#1f1f23] text-zinc-300 uppercase font-semibold text-xs border-b border-zinc-700"
                  {...props}
                >
                  {children}
                </thead>
              );
            },

            tbody({ children, ...props }) {
              return (
                <tbody className="divide-y divide-zinc-700/50 bg-[#18181b]" {...props}>
                  {children}
                </tbody>
              );
            },

            tr({ children, ...props }) {
              return (
                <tr className="hover:bg-zinc-800/40 transition-colors" {...props}>
                  {children}
                </tr>
              );
            },

            th({ children, ...props }) {
              return (
                <th
                  className="py-3 whitespace-nowrap border-b border-zinc-700"
                  style={{ paddingLeft: '20px', paddingRight: '16px', ...(props.style || {}) }}
                  {...props}
                >
                  {children}
                </th>
              );
            },

            td({ children, ...props }) {
              let textContent = "";
              React.Children.forEach(children, child => {
                if (typeof child === 'string') {
                  textContent += child;
                }
              });

              const trimmedText = textContent.trim().toLowerCase();
              const isColor = [
                'red', 'blue', 'green', 'yellow', 'orange', 'purple',
                'black', 'white', 'gray', 'pink', 'brown', 'cyan', 'magenta'
              ].includes(trimmedText);

              const customStyle = { paddingLeft: '20px', paddingRight: '16px', ...(props.style || {}) };

              if (isColor) {
                customStyle.backgroundColor = trimmedText;
                customStyle.color = ['yellow', 'white', 'pink', 'cyan'].includes(trimmedText) ? 'black' : 'white';
              }

              return (
                <td
                  className="py-3 align-top"
                  style={customStyle}
                  {...props}
                >
                  {children}
                </td>
              );
            },



          }
        ), [lightTheme, processingFileId, handleExcelPreview]);

  return (
    <div className="relative group">
      <div className={`markdown-container rounded-md p-1 prose md:prose-sm lg:prose-sm  ${!lightTheme && "prose-invert"} max-w-none text-[#f0f0ee]`}>

        <ReactMarkdown
          className="text-[15px] leading-[1.7]"
          remarkPlugins={REMARK_PLUGINS}
          rehypePlugins={REHYPE_PLUGINS}
          components={markdownComponents}
        >
          {markdown}
        </ReactMarkdown>

      </div>

      {isAssistant && !lightTheme && (
        <div className="absolute -bottom-1 -right-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={handleCopyToClipboard}
            aria-label={isCopied ? 'Content copied' : 'Copy content'}
            className={`p-1.5 rounded-md ${isCopied
              ? 'bg-green-700 text-white'
              : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
          >
            {isCopied ? <Check size={14} /> : <Copy size={14} />}
          </button>
        </div>
      )}
      <ExcelPreviewModal
        isOpen={excelPreviewModal.isOpen}
        onClose={closeExcelPreview}
        file={excelPreviewModal.file}
      />
    </div>
  );
});

export default MarkdownRenderer;